/**
 * The drafter's model table (SUP-273 P1) — hearth's `shared/models.ts`
 * shape: one append-only list carrying everything the host prices off a
 * model id, so the picker and the DO cannot disagree. Context windows feed
 * the compaction trigger only, never a provider call, so round numbers are
 * fine.
 */
import type { ModelClass } from "playground/skills/frontmatter";
import { invariant } from "ts-invariant";

export type DrafterProvider =
  | "anthropic"
  | "openai"
  | "google"
  | "fireworks"
  | "test";

/**
 * What a delegate asks for instead of a model: the CAPABILITY it wants, which
 * the registry answers with today's model for it ({@link delegateTierModelId}).
 * The tool schema names these and never a model id, so the agent is never told
 * a version string — it was being told `claude-opus-5`, and it invented
 * `claude-opus-4-6` from the shape of it. A version we no longer offer (or
 * never did) can't be spelled here, and bumping a model bumps what `frontier`
 * means with no schema, prompt or doc to follow it.
 *
 * ONE type with a SKILL.md's `model:` frontmatter: a skill wanting `frontier`
 * and a delegate run at `frontier` are the same statement.
 */
export type DelegateTier = ModelClass;

export interface DrafterModel {
  id: string;
  label: string;
  provider: DrafterProvider;
  contextWindow: number;
  /** Who made the model, which is not who serves it: Fireworks hosts Qwen and
   *  Moonshot. The picker groups by this and draws `chefSlug`'s logo. */
  chef: string;
  chefSlug: string;
  /** The delegate tier this entry serves, if any. At most one offered model
   *  holds each tier; the rest are the human's to pin, not a delegate's. */
  tier?: DelegateTier;
}

export const DRAFTER_MODELS: readonly DrafterModel[] = [
  {
    // The default (see DEFAULT_DRAFTER_MODEL_ID).
    id: "google:gemini-4-argon",
    label: "Gemini 4 Argon",
    provider: "google",
    contextWindow: 1_048_576,
    chef: "Google",
    chefSlug: "google",
  },
  {
    id: "google:gemini-3.8-flash",
    label: "Gemini 3.8 Flash",
    provider: "google",
    contextWindow: 1_048_576,
    chef: "Google",
    chefSlug: "google",
    // The `fast` delegate: a survey of a large input is the price, not the
    // depth (every offered entry now has a ~1M window, so the window is not
    // what separates the tiers). Transcribing a planset is the opposite and
    // asks for `frontier` (the `specification` skill says so).
    tier: "fast",
  },
  {
    id: "anthropic:claude-opus-5-5",
    label: "Claude Opus 5.5",
    provider: "anthropic",
    contextWindow: 1_000_000,
    chef: "Anthropic",
    chefSlug: "anthropic",
    tier: "frontier",
  },
  {
    id: "anthropic:claude-sonnet-5-5",
    label: "Claude Sonnet 5.5",
    provider: "anthropic",
    contextWindow: 1_000_000,
    chef: "Anthropic",
    chefSlug: "anthropic",
  },
  {
    id: "openai:gpt-6-astra",
    label: "GPT-6 Astra",
    provider: "openai",
    contextWindow: 1_050_000,
    chef: "OpenAI",
    chefSlug: "openai",
  },
  {
    id: "openai:gpt-6.1-sol",
    label: "GPT-6.1 Sol",
    provider: "openai",
    contextWindow: 1_050_000,
    chef: "OpenAI",
    chefSlug: "openai",
  },
  {
    id: "openai:gpt-6-luna",
    label: "GPT-6 Luna",
    provider: "openai",
    contextWindow: 1_050_000,
    chef: "OpenAI",
    chefSlug: "openai",
    // The `cheap` delegate: mechanical work, an order of magnitude under
    // `fast` on every token type. The 5.6 entry it replaced is retired below:
    // same window, half the rate card, and better at the rung this package
    // actually runs. Nothing here pins `reasoning_effort` (turn.ts shares one
    // PROVIDER_OPTIONS across every model, so an OpenAI entry runs at OpenAI's
    // `medium` default), and that is where the swap is measured: Artificial
    // Analysis v4.3.2 scores 6 Luna 29 at `medium` against 5.6 Luna's 25, at
    // 143 against 124 tok/s. The two are level at `high` (32), which is the
    // rung hearth pins and this package does not.
    tier: "cheap",
  },
  {
    id: "fireworks:accounts/fireworks/models/qwen3p6-plus",
    label: "Qwen3.6 Plus",
    provider: "fireworks",
    contextWindow: 262_144,
    chef: "Qwen",
    chefSlug: "fireworks-ai",
  },
  {
    // Kimi K2.7 Code's replacement (Fireworks decommissions the serverless
    // K2.7/K2.6 slugs on 2026-09-25): same chef, same open-weights frontier
    // slot, a much wider window.
    id: "fireworks:accounts/fireworks/models/kimi-k3",
    label: "Kimi K3",
    provider: "fireworks",
    contextWindow: 1_048_576,
    chef: "Moonshot",
    chefSlug: "fireworks-ai",
  },
];

/** The chefs in the order the picker groups them, which is the list's order. */
export const DRAFTER_CHEFS: readonly string[] = [
  ...new Set(DRAFTER_MODELS.map((m) => m.chef)),
];

/**
 * The model a new thread pins when nothing pinned it (`providers.ts`
 * `seedModelId`), which is the list's first entry rather than a second
 * declaration of the same fact. Its provider key gates it: with no
 * `GOOGLE_GENERATIVE_AI_API_KEY` provisioned a new thread seeds the echo model,
 * so moving the default to another provider is an ops step, not just an edit.
 */
export const DEFAULT_DRAFTER_MODEL_ID = DRAFTER_MODELS[0]!.id;

/** The tiers a delegate may ask for, which is what the offered list serves. */
export const DELEGATE_TIERS: readonly DelegateTier[] = [
  ...new Set(DRAFTER_MODELS.flatMap((m) => (m.tier ? [m.tier] : []))),
];

/**
 * Today's model for a delegate tier. The OFFERED list only, so a retired
 * model is unreachable however long ago the tier was named, and a bump of the
 * frontier entry moves every future delegate with it.
 */
export function delegateTierModelId(tier: DelegateTier): string {
  const model = DRAFTER_MODELS.find((m) => m.tier === tier);
  invariant(model, `no offered drafter model serves the "${tier}" tier`);
  return model.id;
}

/**
 * Models we no longer OFFER but must still RESOLVE. A chat pins its model at
 * creation and `DrafterHostDO.defaultModelId()` reads that id back off the row
 * forever, so retiring an entry by deleting it doesn't fail loudly: the window
 * falls to {@link DEFAULT_CONTEXT_WINDOW} and the thread compacts many times too
 * early, throwing away detail. Append-only, and the same {@link DrafterModel}
 * shape as an offered entry so a retirement can't drop a field a live turn
 * still needs (hearth's `shared/models.ts` carries the same pair).
 */
const RETIRED_DRAFTER_MODELS: readonly DrafterModel[] = [
  {
    // Superseded by 3.8 Flash (2026-09-02) at the same window.
    id: "google:gemini-3.7-flash",
    label: "Gemini 3.7 Flash",
    provider: "google",
    contextWindow: 1_048_576,
    chef: "Google",
    chefSlug: "google",
  },
  {
    // Superseded by GPT-6 Astra (2026-09-04) at a 2.6x window.
    id: "openai:gpt-5.6-sol",
    label: "GPT-5.6 Sol",
    provider: "openai",
    contextWindow: 400_000,
    chef: "OpenAI",
    chefSlug: "openai",
  },
  {
    // Superseded by GPT-6 Luna (2026-09-22) at the same window: on Artificial
    // Analysis v4.3.2 the successor scores higher at the `medium` default this
    // package runs unpinned (29 against 25) and level at `high` (32), while
    // answering faster and costing half as much. The `cheap` tier moved with
    // no trade to weigh.
    id: "openai:gpt-5.6-luna",
    label: "GPT-5.6 Luna",
    provider: "openai",
    contextWindow: 1_050_000,
    chef: "OpenAI",
    chefSlug: "openai",
  },
  {
    // Superseded by Kimi K3: Fireworks decommissions the serverless
    // `kimi-k2p7-code` slug on 2026-09-25.
    id: "fireworks:accounts/fireworks/models/kimi-k2p7-code",
    label: "Kimi K2.7 Code",
    provider: "fireworks",
    contextWindow: 262_144,
    chef: "Moonshot",
    chefSlug: "fireworks-ai",
  },
  {
    // Superseded by Opus 5.5 (2026-09-28) at the same window, and cheaper.
    id: "anthropic:claude-opus-5",
    label: "Claude Opus 5",
    provider: "anthropic",
    contextWindow: 1_000_000,
    chef: "Anthropic",
    chefSlug: "anthropic",
  },
  {
    // Superseded by Sonnet 5.5 (2026-09-28) at the same window.
    id: "anthropic:claude-sonnet-4-6",
    label: "Claude Sonnet 4.6",
    provider: "anthropic",
    contextWindow: 1_000_000,
    chef: "Anthropic",
    chefSlug: "anthropic",
  },
];

const DEFAULT_CONTEXT_WINDOW = 128_000;

/** The provider prefix of a `provider:path` model id ("test" included). An
 *  unqualified id reads as anthropic, matching the Bun drafter's rule. */
export function providerOf(modelId: string): DrafterProvider {
  const prefix = modelId.split(":")[0];
  switch (prefix) {
    case "anthropic":
    case "openai":
    case "google":
    case "fireworks":
    case "test":
      return prefix;
    default:
      return "anthropic";
  }
}

/** The provider-facing model path (everything after the first colon). */
export function modelPath(modelId: string): string {
  const idx = modelId.indexOf(":");
  return idx === -1 ? modelId : modelId.slice(idx + 1);
}

/**
 * The entry for `modelId`, retired ones included: what a chat pinned long ago
 * needs, since {@link DRAFTER_MODELS} is only what we still OFFER. Every read
 * of a pinned id goes through here, so a retirement never costs a thread its
 * label, its logo or its window. Retired entries stay unexported so the picker
 * cannot iterate them back into the list.
 */
export function drafterModel(modelId: string): DrafterModel | undefined {
  return (
    DRAFTER_MODELS.find((m) => m.id === modelId) ??
    RETIRED_DRAFTER_MODELS.find((m) => m.id === modelId)
  );
}

export function contextWindowFor(modelId: string): number {
  return drafterModel(modelId)?.contextWindow ?? DEFAULT_CONTEXT_WINDOW;
}
